import '~/index.css';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import {
  browserTracingIntegration,
  init,
  setUser,
  thirdPartyErrorFilterIntegration,
  withProfiler,
} from '@sentry/react';
import { MotionConfig } from 'motion/react';
import posthog from 'posthog-js';
import { ComponentType, StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { v4 } from 'uuid';

import NoPrerender from '~/modules/Elements/NoPrerender';
import isDev from '~/modules/utils/isDev';
import isE2E from '~/modules/utils/isE2E';
import isPreRendering from '~/modules/utils/isPreRendering';
import sentryIgnoreErrors from '~/modules/utils/sentryIgnoreErrors';
import storage from '~/modules/utils/storage';

interface RenderReactAppOptions {
  createAnonymousUserName?: () => string;
}

const isSentryEnabled = !!import.meta.env.VITE_APP_SENTRY_DSN_URL;
let monitoringInitialized = false;

function initMonitoring({ createAnonymousUserName }: RenderReactAppOptions) {
  if (monitoringInitialized) return;
  monitoringInitialized = true;

  if (isSentryEnabled) {
    init({
      integrations: [
        browserTracingIntegration({
          enableInp: true,
        }),
        thirdPartyErrorFilterIntegration({
          filterKeys: ['allkaraoke-party-sentry-key'],
          behaviour: 'apply-tag-if-contains-third-party-frames',
        }),
      ],

      dsn: import.meta.env.VITE_APP_SENTRY_DSN_URL,
      ignoreErrors: sentryIgnoreErrors,
      tracesSampleRate: isE2E() ? 0 : 0.01,
      environment: isDev() ? 'development' : isE2E() ? 'e2e' : 'production',
      tunnel: '/stry-tunnel',
    });
  }

  if (!isE2E() && import.meta.env.VITE_APP_POSTHOG_KEY) {
    posthog.init(import.meta.env.VITE_APP_POSTHOG_KEY, {
      api_host: '/ph-data',
      loaded: (ph) => {
        let storedUser = storage.local.getItem('posthog-user-id');
        if (!storedUser) {
          storedUser = v4();
          storage.local.setItem('posthog-user-id', storedUser);
        }

        ph.identify(storedUser);

        if (createAnonymousUserName) {
          let storedName = storage.local.getItem('posthog-user-name');
          if (!storedName) {
            storedName = createAnonymousUserName();
            storage.local.setItem('posthog-user-name', storedName);
            ph.alias(storedName, storedUser);
          }
        }

        if (isSentryEnabled) {
          setUser({ id: storedUser });
        }
      },
    });
  }
}

const emotionCache = createCache({
  key: 'ec',
  speedy: !isPreRendering,
});

const LazyToastContainer = lazy(() =>
  import('react-toastify').then(({ ToastContainer }) => ({ default: ToastContainer })),
);

export function renderReactApp(App: ComponentType, options: RenderReactAppOptions = {}) {
  initMonitoring(options);

  const container = document.getElementById('root');
  const root = createRoot(container!);
  const AppWithProfiler = withProfiler(App);

  root.render(
    <StrictMode>
      <MotionConfig
        transition={isE2E() ? { duration: 0.001 } : undefined}
        reducedMotion={isE2E() ? 'always' : undefined}>
        <CacheProvider value={emotionCache}>
          <AppWithProfiler />
          <NoPrerender>
            <Suspense>
              <LazyToastContainer position="bottom-left" theme="colored" limit={3} />
            </Suspense>
          </NoPrerender>
        </CacheProvider>
      </MotionConfig>
    </StrictMode>,
  );
}
